import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Users, CheckCircle2, Loader2, AlertTriangle, ArrowRight, CreditCard } from 'lucide-react';
import Modal from '../../common/Modal/Modal';
import Button from '../../common/Button/Button';
import ModalEmptyState from '../shared/ModalEmptyState';
import { dmUnlockApi } from '../../../api/dmUnlock';
import { useBalance } from '../../../hooks/useBalance';
import { useDMUnlockPolling } from '../../../hooks/useDMUnlockPolling';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { clearRecentCompletion, setModalOwnedBatchId } from '../../../store/slices/dmUnlockSlice';
import EnrichmentConfirmScreen from '../shared/EnrichmentConfirmScreen';
import CompanySelector from '../shared/CompanySelector';
import ProcessingScreen from '../shared/ProcessingScreen';
import EnrichmentResults, { FindMoreCompany } from '../shared/EnrichmentResults';
import type { Scope } from '../ScopeToggle/ScopeToggle';

import { convertFiltersToApiFormat } from '../../../utils/filterConversion';
import { countUniqueDecisionMakers, selectPlacesWithinBudget } from '../../../utils/dmUnlockSelection';

import { usePostHog } from 'posthog-js/react';
import {
  UnlockEstimateResponse,
  BulkUnlockResponse,
  ResultsFilter,
  BatchStatusResponse,
} from '../../../types/results.types';

interface BulkDMUnlockModalProps {
  isOpen: boolean;
  onClose: () => void;
  personIds: number[];
  searchId?: number;
  filters?: ResultsFilter;
  onSuccess?: (response: BulkUnlockResponse) => void;
  showCompanySelect?: boolean;
  defaultScope?: Scope;
  initialEstimate?: UnlockEstimateResponse;
  initialDmUnlocksRemaining?: number;
  onUpgradeClick?: () => void;
  /** Total result count reflecting active filters — used for the "All filtered results" tab label. */
  totalFilteredCount?: number;
  reopenBatchId?: string | null;
}

type ModalStep = 'estimate' | 'select' | 'confirm' | 'processing' | 'results';

const BulkDMUnlockModal: React.FC<BulkDMUnlockModalProps> = ({
  isOpen,
  onClose,
  personIds,
  searchId,
  filters,
  onSuccess,
  showCompanySelect = false,
  defaultScope,
  initialEstimate,
  initialDmUnlocksRemaining: _initialDmUnlocksRemaining,
  onUpgradeClick,
  totalFilteredCount,
  reopenBatchId,
}) => {
  const [scope, setScope] = useState<Scope>('page');
  const supportsBulkScope = Boolean(searchId && filters);

  useLayoutEffect(() => {
    if (isOpen) {
      intendedScopeRef.current = defaultScope ?? 'page';
      setScope(defaultScope ?? 'page');
    }
  }, [isOpen, defaultScope]);

  const [step, setStep] = useState<ModalStep>(initialEstimate ? 'confirm' : 'estimate');
  const [stepDirection, setStepDirection] = useState<'forward' | 'back'>('forward');

  const goForward = (next: ModalStep) => { setStepDirection('forward'); setStep(next); };
  const goBack = (next: ModalStep) => { setStepDirection('back'); setStep(next); };

  const [estimate, setEstimate] = useState<UnlockEstimateResponse | null>(initialEstimate ?? null);
  const [selectedPlaceIds, setSelectedPlaceIds] = useState<Set<number>>(new Set());
  const [autoSelectedSubset, setAutoSelectedSubset] = useState(false);
  const [userModifiedSelection, setUserModifiedSelection] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resultsSummarySnapshot, setResultsSummarySnapshot] = useState<BatchStatusResponse | null>(null);
  const [findMoreEstimate, setFindMoreEstimate] = useState<UnlockEstimateResponse | null>(null);
  const [findMoreLoading, setFindMoreLoading] = useState(false);

  const { balance, balanceLoading, balanceError, refreshBalance } = useBalance();
  const dmBalance = balance?.dm_unlocks_remaining ?? null;
  const dmUnlocksUsed = useAppSelector((state) => (
    state.results.summary?.ready_personal_count ?? state.results.summary?.dm_unlocked ?? 0
  ));

  useEffect(() => {
    if (!estimate || dmBalance === null || userModifiedSelection) return;
    if (step === 'processing' || step === 'results') return;
    const totalPeople = countUniqueDecisionMakers(estimate.places);
    if (totalPeople > dmBalance) {
      setSelectedPlaceIds(selectPlacesWithinBudget(estimate.places, dmBalance));
      setAutoSelectedSubset(true);
      setStep('select');
    } else {
      setSelectedPlaceIds(new Set(estimate.places.map((p) => p.place_id)));
      setAutoSelectedSubset(false);
      setStep(showCompanySelect ? 'select' : 'confirm');
    }
  }, [estimate, dmBalance, showCompanySelect]); // eslint-disable-line react-hooks/exhaustive-deps

  const effectivePersonIds = estimate
    ? estimate.places
        .filter((place) => selectedPlaceIds.has(place.place_id))
        .flatMap((place) => place.person_ids)
    : [];
  const selectedUniqueCount = estimate
    ? countUniqueDecisionMakers(estimate.places, selectedPlaceIds)
    : 0;

  const dispatch = useAppDispatch();
  const posthog = usePostHog();
  const { startPolling, progress, batchStatus, completedBatchStatus, batchTotal, isPolling } = useDMUnlockPolling({
    searchId,
    personIds,
    kind: 'bulk',
    attachBatchId: reopenBatchId,
  });
  const startedBatchIdRef = useRef<string | null>(null);
  const intendedScopeRef = useRef<Scope>(defaultScope ?? 'page');
  const loadingForScopeRef = useRef<Scope | null>(null);

  const batchStatusForDisplay = batchStatus ?? completedBatchStatus;

  useEffect(() => {
    if (!isOpen || !reopenBatchId) return;
    startedBatchIdRef.current = reopenBatchId;
    dispatch(setModalOwnedBatchId(reopenBatchId));
    if (isPolling) {
      setStep('processing');
      setLoading(false);
    } else if (completedBatchStatus?.batch_id === reopenBatchId) {
      setResultsSummarySnapshot(completedBatchStatus);
      setStep('results');
      setLoading(false);
    }
  }, [isOpen, reopenBatchId, isPolling, completedBatchStatus?.batch_id, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const getPeopleCountInEstimate = () => {
    if (!estimate) return undefined;
    return countUniqueDecisionMakers(estimate.places);
  };

  const handleScopeChange = (nextScope: Scope) => {
    if (!supportsBulkScope || nextScope === scope) return;
    intendedScopeRef.current = nextScope;
    loadingForScopeRef.current = null;
    setScope(nextScope);
    setEstimate(null);
    setSelectedPlaceIds(new Set());
    setError(null);
  };

  const renderScopeTabs = () => {
    const allTabCount = totalFilteredCount ?? (scope === 'all' ? getPeopleCountInEstimate() : undefined);
    const pageTabCount = scope === 'page' ? (getPeopleCountInEstimate() ?? personIds.length) : personIds.length;

    return (
      <div className="-mx-6 flex gap-0.5 border-b border-gray-200 px-6">
        <button
          type="button"
          onClick={() => handleScopeChange('all')}
          className={`mb-[-1px] border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            scope === 'all'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-600 hover:text-gray-800'
          }`}
        >
          {`All filtered results${allTabCount !== undefined ? ` (${allTabCount.toLocaleString()})` : ''}`}
        </button>
        <button
          type="button"
          onClick={() => handleScopeChange('page')}
          className={`mb-[-1px] border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            scope === 'page'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-600 hover:text-gray-800'
          }`}
        >
          {`Current page (${pageTabCount.toLocaleString()})`}
        </button>
      </div>
    );
  };

  useEffect(() => {
    const live = completedBatchStatus ?? batchStatus;
    if (live?.status === 'COMPLETED') {
      setResultsSummarySnapshot(live);
    }
  }, [completedBatchStatus, batchStatus]);

  useEffect(() => {
    if (step !== 'confirm' || loading || balanceLoading) return;
    if (dmBalance !== 0) return;
    posthog?.capture('upgrade_cta_shown', {
      source: 'dm_unlock_exhausted',
      context: 'bulk',
      search_id: searchId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, loading, balanceLoading, dmBalance]);

  const stepRef = useRef(step);
  useEffect(() => { stepRef.current = step; }, [step]);
  const estimateRef = useRef(estimate);
  useEffect(() => { estimateRef.current = estimate; }, [estimate]);

  const togglePlace = (placeId: number) => {
    setUserModifiedSelection(true);
    setSelectedPlaceIds((prev) => {
      const next = new Set(prev);
      next.has(placeId) ? next.delete(placeId) : next.add(placeId);
      return next;
    });
  };

  const toggleAll = () => {
    if (!estimate) return;
    setUserModifiedSelection(true);
    setSelectedPlaceIds(
      selectedPlaceIds.size === estimate.places.length
        ? new Set()
        : new Set(estimate.places.map((place) => place.place_id))
    );
  };

  const handleSelectMax = () => {
    if (!estimate || dmBalance === null) return;
    setUserModifiedSelection(true);
    setSelectedPlaceIds(selectPlacesWithinBudget(estimate.places, dmBalance));
  };

  useEffect(() => {
    const isMyBatchCompleted = (
      (startedBatchIdRef.current !== null && (
        completedBatchStatus?.batch_id === startedBatchIdRef.current ||
        batchStatus?.batch_id === startedBatchIdRef.current
      )) ||
      stepRef.current === 'processing'
    ) && (
      completedBatchStatus?.status === 'COMPLETED' ||
      batchStatus?.status === 'COMPLETED'
    );
    if (!isPolling && isMyBatchCompleted) {
      refreshBalance();
      goForward('results');
      setLoading(false);
      if (searchId && filters) {
        setFindMoreLoading(true);
        setFindMoreEstimate(null);
        dmUnlockApi
          .estimateCost({ bulk: true, search_id: searchId, ...convertFiltersToApiFormat(filters) })
          .then((est) => setFindMoreEstimate(est))
          .catch(() => setFindMoreEstimate(null))
          .finally(() => setFindMoreLoading(false));
      }
    }
  }, [isPolling, batchStatus?.status, batchStatus?.batch_id, completedBatchStatus?.status, completedBatchStatus?.batch_id, dispatch, posthog, refreshBalance]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen || reopenBatchId || (scope === 'page' && personIds.length === 0)) return;
    if (isPolling) {
      setStep('processing');
      setLoading(false);
      return;
    }
    const isMyBatchCompleted = (
      (startedBatchIdRef.current !== null && (
        completedBatchStatus?.batch_id === startedBatchIdRef.current ||
        batchStatus?.batch_id === startedBatchIdRef.current
      )) ||
      stepRef.current === 'processing'
    ) && (
      completedBatchStatus?.status === 'COMPLETED' ||
      batchStatus?.status === 'COMPLETED'
    );
    if (isMyBatchCompleted) {
      goForward('results');
      setLoading(false);
      return;
    }
    if (
      stepRef.current === 'processing' ||
      stepRef.current === 'results'
    ) {
      return;
    }
    if (stepRef.current === 'confirm' && estimateRef.current) {
      return;
    }
    if (!estimateRef.current) {
      if (scope !== intendedScopeRef.current) return;
      if (loadingForScopeRef.current === scope) return;
      loadingForScopeRef.current = scope;
      void loadEstimate({ preserveCurrentStep: false });
    }
  }, [isOpen, personIds, isPolling, batchStatus?.status, batchStatus?.batch_id, completedBatchStatus?.status, completedBatchStatus?.batch_id, scope]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEstimate = async ({
    preserveCurrentStep = false,
    estimateScope,
  }: { preserveCurrentStep?: boolean; estimateScope?: Scope } = {}) => {
    setLoading(true);
    setError(null);
    if (!preserveCurrentStep) {
      setStep('estimate');
    }

    const activeScope = estimateScope ?? scope;

    try {
      const estimateRequest =
        activeScope === 'all' && searchId && filters
          ? { bulk: true, search_id: searchId, ...convertFiltersToApiFormat(filters) }
          : { person_ids: personIds, search_id: searchId };
      const estimateResponse = await dmUnlockApi.estimateCost(estimateRequest);
      setEstimate(estimateResponse);
      if (estimateResponse.places.length > 0) {
        const totalPeople = countUniqueDecisionMakers(estimateResponse.places);
        const currentBalance = dmBalance ?? 0;
        if (totalPeople > currentBalance) {
          setSelectedPlaceIds(selectPlacesWithinBudget(estimateResponse.places, currentBalance));
          setAutoSelectedSubset(true);
          setStep('select');
        } else {
          setSelectedPlaceIds(new Set(estimateResponse.places.map((p) => p.place_id)));
          setAutoSelectedSubset(false);
          setStep(showCompanySelect ? 'select' : 'confirm');
        }
      } else {
        setStep('confirm');
      }
    } catch (err: any) {
      console.error('Failed to load estimate:', err);
      setError(err.response?.data?.detail || 'Failed to load estimate. Please try again.');
      setStep('estimate');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    posthog?.capture('enrich_modal_confirmed', {
      person_count: showCompanySelect ? selectedUniqueCount : personIds.length,
      bulk_mode: scope === 'all',
    });
    setLoading(true);
    setError(null);
    setResultsSummarySnapshot(null);

    const unlockRequest = scope === 'all' && searchId && filters && !showCompanySelect
      ? { bulk: true, search_id: searchId, ...convertFiltersToApiFormat(filters) }
      : { person_ids: showCompanySelect ? effectivePersonIds : personIds, search_id: searchId };

    try {
      const response = await dmUnlockApi.unlockBulk(searchId!, unlockRequest);

      if (response.batch_id && response.queued > 0) {
        startedBatchIdRef.current = response.batch_id;
        dispatch(setModalOwnedBatchId(response.batch_id));
        const resolvedIds = scope === 'all' ? [] : (showCompanySelect ? effectivePersonIds : personIds);
        startPolling(response.batch_id, searchId ?? undefined, resolvedIds, response.total);
        goForward('processing');
      } else {
        const succeeded = response.results.filter((item: any) => item.email).length;
        const failed = Math.max(0, response.total - succeeded);
        setResultsSummarySnapshot({
          batch_id: response.batch_id,
          status: 'COMPLETED',
          progress_percent: 100,
          total: response.total,
          succeeded,
          failed,
          pending: 0,
          processing: 0,
        });
        refreshBalance();
        goForward('results');
        setLoading(false);
        if (onSuccess) {
          onSuccess(response);
        }
      }
    } catch (err: any) {
      console.error('Failed to unlock:', err);
      if (err.response?.status === 402 && onUpgradeClick) {
        refreshBalance();
        onUpgradeClick();
        setLoading(false);
        return;
      }
      setError(err.response?.data?.detail || 'Failed to find owner emails. Please try again.');
      setStep('confirm');
      setLoading(false);
    }
  };

  const handleFindMore = () => {
    if (!findMoreEstimate) return;
    setEstimate(findMoreEstimate);
    setFindMoreEstimate(null);
    setUserModifiedSelection(false);
    setAutoSelectedSubset(false);
    setResultsSummarySnapshot(null);
    startedBatchIdRef.current = null;
    goForward('select');
  };

  const resetState = () => {
    stepRef.current = 'estimate';
    estimateRef.current = null;
    startedBatchIdRef.current = null;
    intendedScopeRef.current = 'page';
    loadingForScopeRef.current = null;
    setResultsSummarySnapshot(null);
    setFindMoreEstimate(null);
    setFindMoreLoading(false);
    setStep('estimate');
    setEstimate(null);
    setError(null);
    setScope('page');
    setUserModifiedSelection(false);
    setAutoSelectedSubset(false);
    setSelectedPlaceIds(new Set());
  };

  const handleClose = () => {
    const completedBatchId = resultsSummarySnapshot?.batch_id ?? startedBatchIdRef.current;
    if (completedBatchId && stepRef.current === 'results') {
      dispatch(clearRecentCompletion(completedBatchId));
    }
    dispatch(setModalOwnedBatchId(null));
    onClose();
  };

  const renderEstimateSkeleton = () => (
    <div className="h-[550px] space-y-4 overflow-hidden text-base" aria-busy="true" aria-label="Calculating estimate">
      <div className="flex flex-wrap items-center gap-2 pt-4">
        <div className="h-5 w-5 animate-pulse rounded-full bg-gray-200" />
        <div className="h-5 w-12 animate-pulse rounded bg-gray-200" />
        <div className="h-4 w-44 animate-pulse rounded bg-gray-100" />
        <div className="h-4 w-32 animate-pulse rounded bg-gray-100" />
      </div>

      <div className="space-y-1">
        <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
        <div className="h-4 w-80 max-w-full animate-pulse rounded bg-gray-100" />
      </div>

      <div className="rounded-lg border border-gray-200 bg-white px-4 py-3.5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="h-5 w-5 animate-pulse rounded bg-gray-200" />
            <div className="h-4 w-20 animate-pulse rounded bg-gray-200" />
          </div>
          <div className="h-4 w-28 animate-pulse rounded bg-gray-100" />
        </div>
      </div>

      <div className="space-y-2">
        {[0, 1, 2].map((item) => (
          <div key={item} className="rounded-lg border border-gray-200 bg-white px-4 py-3">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="flex flex-1 items-center gap-3">
                <div className="h-5 w-5 animate-pulse rounded bg-gray-200" />
                <div className="h-4 w-56 max-w-full animate-pulse rounded bg-gray-200" />
              </div>
              <div className="h-7 w-12 animate-pulse rounded-full bg-gray-100" />
            </div>
            <div className="ml-8 h-4 w-64 max-w-[80%] animate-pulse rounded bg-gray-100" />
          </div>
        ))}
      </div>

      <div className="flex gap-2 pt-2">
        <div className="h-10 flex-1 animate-pulse rounded-md bg-gray-100" />
        <div className="h-10 flex-[2] animate-pulse rounded-md bg-gray-200" />
      </div>
    </div>
  );

  const renderEstimate = () => {
    // Page scope with no person IDs: show empty state instead of blank
    if (scope === 'page' && personIds.length === 0 && !loading) {
      if (supportsBulkScope) {
        return (
          <ModalEmptyState
            heading="No contacts to enrich on this page"
            body="There are no contacts on this page that need enrichment."
            ctaLabel="View all filtered results"
            onCtaClick={() => handleScopeChange('all')}
            icon={<Users className="h-6 w-6 text-gray-400" aria-hidden />}
          />
        );
      }
      return (
        <ModalEmptyState
          heading="No contacts to enrich"
          body="There are no contacts that need enrichment."
          icon={<Users className="h-6 w-6 text-gray-400" aria-hidden />}
          terminal
        />
      );
    }

    if (loading) {
      return renderEstimateSkeleton();
    }

    if (error) {
      return (
        <div className="flex flex-col items-center py-8">
          <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
          <p className="text-base text-red-600 mb-4">{error}</p>
          <button
            type="button"
            onClick={() => loadEstimate()}
            className="px-4 py-2 text-base bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Try Again
          </button>
        </div>
      );
    }

    return null;
  };

  const renderSelect = () => {
    // Page scope, no contacts → empty state (also catches step=select with null estimate)
    if (scope === 'page' && personIds.length === 0) {
      if (supportsBulkScope) {
        return (
          <ModalEmptyState
            heading="No contacts to enrich on this page"
            body="There are no contacts on this page that need enrichment."
            ctaLabel="View all filtered results"
            onCtaClick={() => handleScopeChange('all')}
            icon={<Users className="h-6 w-6 text-gray-400" aria-hidden />}
          />
        );
      }
      return (
        <ModalEmptyState
          heading="No contacts to enrich"
          body="There are no contacts that need enrichment."
          icon={<Users className="h-6 w-6 text-gray-400" aria-hidden />}
          terminal
        />
      );
    }

    if (!estimate) return null;

    // All places already enriched / none available in this scope
    if (estimate.places.length === 0) {
      return (
        <ModalEmptyState
          heading="Nothing left to enrich in this search"
          body="All contacts in this search have already been enriched."
          icon={<Users className="h-6 w-6 text-gray-400" aria-hidden />}
          terminal
        />
      );
    }

    const allSelected = selectedPlaceIds.size === estimate.places.length;
    const selectedPeopleCount = selectedUniqueCount;
    const remainingAfterSelection = dmBalance !== null ? dmBalance - selectedPeopleCount : null;
    const overBudget = remainingAfterSelection !== null && remainingAfterSelection < 0;
    const noCredits = dmBalance === 0;

    const canProceed =
      !balanceLoading &&
      !balanceError &&
      !overBudget &&
      !noCredits &&
      selectedPeopleCount > 0;

    const ctaLabel = () => {
      if (balanceLoading) return 'Loading balance…';
      if (balanceError) return 'Balance unavailable — retry above';
      if (noCredits) return 'No credits available';
      if (overBudget) {
        const over = selectedPeopleCount - (dmBalance ?? 0);
        return `Deselect ${over} to continue`;
      }
      if (selectedPeopleCount === 0) return 'Select companies to continue';
      return `Find emails for ${selectedPeopleCount} ${selectedPeopleCount === 1 ? 'person' : 'people'}`;
    };

    const sortedPlaces = estimate.places;

    return (
      <div className="flex max-h-[580px] flex-col text-base" aria-live="polite">
        {/* Scrollable body */}
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pb-2 pt-4">
          {/* Info banner */}
          <div className="flex items-start gap-2.5 rounded-lg border border-blue-200 bg-blue-50 px-3.5 py-2.5">
            <svg className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
            </svg>
            <p className="text-xs font-medium text-blue-700">
              We search for owners, founders and decision-makers emails.
            </p>
          </div>

          {/* Credit stat cards */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 shrink-0 text-purple-600" aria-hidden />
                <div>
                  {balanceLoading ? (
                    <div className="h-4 w-6 animate-pulse rounded bg-gray-200" aria-label="Loading" />
                  ) : (
                    <span className="text-sm font-semibold text-gray-900">
                      {selectedPeopleCount.toLocaleString()} people enrichment{selectedPeopleCount !== 1 ? 's' : ''} available
                    </span>
                  )}
                  <p className="mt-0.5 text-xs text-gray-500">Based on your filters</p>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
              <div className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
                <div>
                  {balanceLoading ? (
                    <div className="h-4 w-10 animate-pulse rounded bg-gray-200" aria-label="Loading" />
                  ) : balanceError ? (
                    <button
                      type="button"
                      onClick={refreshBalance}
                      className="text-xs font-medium text-amber-700 underline hover:no-underline"
                    >
                      Retry balance
                    </button>
                  ) : (
                    <span className={`text-sm font-semibold ${overBudget ? 'text-red-600' : 'text-gray-900'}`}>
                      {dmBalance !== null ? `${dmBalance.toLocaleString()} credit${dmBalance !== 1 ? 's' : ''} available` : '—'}
                    </span>
                  )}
                  <p className="mt-0.5 text-xs text-gray-500">Only used when an email is found</p>
                </div>
              </div>
            </div>
          </div>

          {/* Zero-credits banner */}
          {noCredits && !balanceError && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5">
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" aria-hidden />
              <span className="text-xs font-medium text-amber-700">You have no credits available.</span>
            </div>
          )}

          {/* Over-budget banner — only when user manually over-selected */}
          {overBudget && !noCredits && userModifiedSelection && (
            <div className="flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5">
              <div className="flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" aria-hidden />
                <span className="text-xs font-medium text-amber-700">
                  {dmBalance !== null
                    ? `Over by ${selectedPeopleCount - dmBalance} credit${selectedPeopleCount - dmBalance === 1 ? '' : 's'}. Deselect companies or select the max you can afford.`
                    : 'Selection exceeds your credit balance.'}
                </span>
              </div>
              {dmBalance !== null && dmBalance > 0 && (
                <button
                  type="button"
                  onClick={handleSelectMax}
                  className="flex shrink-0 items-center gap-1 rounded-md border border-amber-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-50"
                >
                  <ArrowRight className="h-3 w-3" aria-hidden />
                  Select max ({dmBalance})
                </button>
              )}
            </div>
          )}

          {/* Company selection header */}
          <div className="flex items-center justify-between gap-3">
            <h4 className="min-w-0 truncate text-sm font-semibold text-gray-900">Select companies to search</h4>
            {selectedPlaceIds.size > 0 && (
              <div className="flex shrink-0 items-center gap-2 text-sm">
                <span className="text-gray-500">{selectedPlaceIds.size} selected</span>
                <button
                  style={{ paddingRight: '5px'}}
                  type="button"
                  onClick={() => {
                    setUserModifiedSelection(true);
                    setSelectedPlaceIds(new Set());
                  }}
                  className="font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  Clear
                </button>
              </div>
            )}
          </div>

          <div
            className="flex cursor-pointer items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3.5 transition-colors hover:border-primary/60 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
            onClick={toggleAll}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                toggleAll();
              }
            }}
            role="button"
            tabIndex={0}
          >
            <label className="flex cursor-pointer items-center gap-2.5" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = !allSelected && selectedPlaceIds.size > 0;
                }}
                onChange={toggleAll}
                className="h-5 w-5 shrink-0 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-base font-medium text-gray-800">Select all</span>
            </label>
            <span className="text-sm text-gray-600">
              {estimate.places.length.toLocaleString()} {estimate.places.length === 1 ? 'company' : 'companies'}
            </span>
          </div>

          <div
            className={`space-y-2 pr-1 transition-opacity ${
              loading ? 'pointer-events-none opacity-60' : ''
            }`}
            aria-busy={loading}
          >
            {sortedPlaces.map((place) => (
              <CompanySelector
                key={place.place_id}
                place={{
                  place_id: place.place_id,
                  place_name: place.place_name,
                  item_count: place.dm_count,
                  people: place.people,
                }}
                selected={selectedPlaceIds.has(place.place_id)}
                onToggle={() => togglePlace(place.place_id)}
                showAvatar
              />
            ))}
          </div>
        </div>

        {/* Three-stat summary footer
        <div className="grid grid-cols-3 gap-2 border-t border-gray-100 pt-3">
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-center">
            <div className="text-base font-extrabold text-gray-900">
              {selectedPlaceIds.size.toLocaleString()}
            </div>
            <div className="mt-0.5 text-[10.5px] font-medium text-gray-400">
              {selectedPlaceIds.size === 1 ? 'Company' : 'Companies'} selected
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-center">
            <div className="text-base font-extrabold text-gray-900">
              {selectedPeopleCount.toLocaleString()}
            </div>
            <div className="mt-0.5 text-[10.5px] font-medium text-gray-400">
              {selectedPeopleCount === 1 ? 'Person' : 'People'} enrichment{selectedPeopleCount !== 1 ? 's' : ''}
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-center">
            <div className={`text-base font-extrabold ${overBudget ? 'text-red-600' : 'text-gray-900'}`}>
              {selectedPeopleCount.toLocaleString()}
            </div>
            <div className="mt-0.5 text-[10.5px] font-medium text-gray-400">
              {overBudget ? 'Over budget' : 'Will be used if email is found'}
            </div>
          </div>
        </div> */}

        {/* Action buttons */}
        <div className="flex gap-2 pt-3">
          <Button onClick={handleClose} variant="secondary" className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={showCompanySelect ? handleConfirm : () => goForward('confirm')}
            disabled={!canProceed}
            isLoading={showCompanySelect && loading}
            variant={canProceed ? 'primary' : 'secondary'}
            className={`flex-[2] !text-base ${
              !canProceed
                ? '!cursor-not-allowed !border-gray-200 !bg-gray-200 !text-gray-400 hover:!bg-gray-200'
                : ''
            }`}
          >
            {ctaLabel()}
          </Button>
        </div>
      </div>
    );
  };

  const renderConfirm = () => {
    if (!estimate) return null;

    const totalToUnlock = showCompanySelect
      ? selectedUniqueCount
      : estimate.already_has_email + estimate.needs_discovery;
    const canUnlock = dmBalance !== null && totalToUnlock !== null ? dmBalance >= totalToUnlock : false;
    const selectedPersonCount = showCompanySelect
      ? selectedUniqueCount
      : estimate.already_unlocked + totalToUnlock;

    return (
      <EnrichmentConfirmScreen
        estimate={estimate}
        estimateLoading={false}
        personCount={selectedPersonCount}
        totalToUnlock={totalToUnlock}
        dmUnlocksRemaining={dmBalance}
        balanceLoading={balanceLoading}
        balanceError={balanceError}
        onRetryBalance={refreshBalance}
        canUnlock={canUnlock}
        estimateError={null}
        submitError={error}
        submitLoading={loading}
        showCompanyBreakdown={!showCompanySelect}
        autoSelectedSubset={autoSelectedSubset}
        onChangeSelection={() => goBack('select')}
        onCancel={handleClose}
        onConfirm={handleConfirm}
        onUpgradeClick={onUpgradeClick}
        dmUnlocksUsed={dmUnlocksUsed}
      />
    );
  };

  const renderProcessing = () => (
    <div className="space-y-4">
      <ProcessingScreen
        progress={progress}
        processed={batchStatusForDisplay ? batchStatusForDisplay.succeeded + batchStatusForDisplay.failed : 0}
        total={batchStatusForDisplay?.total ?? batchTotal}
        succeeded={batchStatusForDisplay?.succeeded}
        failed={batchStatusForDisplay?.failed}
        pending={batchStatusForDisplay?.pending}
      />
      <Button onClick={handleClose} variant="primary" className="w-full">
        Close
      </Button>
    </div>
  );

  const renderResults = () => {
    const summary = resultsSummarySnapshot ?? completedBatchStatus ?? batchStatus;
    if (!summary) return null;

    const findMorePlaces = findMoreEstimate?.places ?? [];
    const findMoreContactCount = countUniqueDecisionMakers(findMorePlaces);
    const findMoreCompanyCount = findMorePlaces.length;
    const hasCredits = (dmBalance ?? 0) > 0;
    const findMoreCompanies: FindMoreCompany[] = findMorePlaces.map((p) => ({
      name: p.place_name,
      count: p.dm_count,
    }));

    return (
      <EnrichmentResults
        successCount={summary.succeeded}
        failedCount={summary.failed}
        totalCount={summary.total}
        foundContacts={summary.found_contacts}
        findMoreLoading={findMoreLoading}
        onFindMore={!findMoreLoading && findMoreContactCount > 0 && hasCredits ? handleFindMore : undefined}
        findMoreContactCount={findMoreContactCount}
        findMoreCompanyCount={findMoreCompanyCount}
        findMoreCompanies={findMoreCompanies}
        onUpgradeClick={!findMoreLoading && findMoreContactCount > 0 && !hasCredits ? onUpgradeClick : undefined}
        onClose={handleClose}
      />
    );
  };

  const getTitle = () => {
    switch (step) {
      case 'estimate': return 'Find owner emails';
      case 'select': return 'Find owner emails';
      case 'confirm': return 'Find owner emails';
      case 'processing': return 'Finding owner emails...';
      case 'results': return 'Owner email discovery complete';
      default: return 'Find owner emails';
    }
  };

  const getTitleIcon = () => {
    switch (step) {
      case 'select':
      case 'confirm':
        return <Users className="h-4 w-4 text-primary" aria-hidden />;
      case 'estimate':
      case 'processing':
        return <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden />;
      case 'results':
        return <CheckCircle2 className="h-4 w-4 text-emerald-600" strokeWidth={2} aria-hidden />;
      default:
        return <Users className="h-4 w-4 text-primary" aria-hidden />;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      afterLeave={resetState}
      size="lg"
      appearance="product"
      title={getTitle()}
      titleIcon={getTitleIcon()}
      titleIconWrapperClassName={step === 'results' ? 'bg-emerald-100' : undefined}
    >
      <div
        className={stepDirection === 'back' ? 'animate-step-back' : 'animate-step-forward'}
        aria-live="polite"
      >
        {(step === 'estimate' || step === 'select') && supportsBulkScope && (
          <div className="mb-4">{renderScopeTabs()}</div>
        )}
        {step === 'estimate' && renderEstimate()}
        {step === 'select' && renderSelect()}
        {step === 'confirm' && renderConfirm()}
        {step === 'processing' && renderProcessing()}
        {step === 'results' && renderResults()}
      </div>
    </Modal>
  );
};

export default BulkDMUnlockModal;
